import type { z } from "zod/v4";

/** How a payload stored under some schema version reads today. */
export type ReadResult<T> =
  | { type: "read"; value: T }
  | { type: "unreadable"; version: number; reason: string }
  /** Written by a newer release than this reader; a reload shows it. */
  | { type: "newer"; version: number };

/** Upgrades a payload from the version it is keyed by to the next one. */
export type Upcasters = Readonly<Record<number, (raw: unknown) => unknown>>;

/**
 * The one way a stored payload is read, wherever it was stored.
 *
 * A missing version is 0: a payload written before versions existed. A version
 * above `current` is not parsed, because an older schema would silently drop
 * the fields it does not know. Otherwise each upcaster from the stored version
 * up to `current` runs, then the schema parses. It never throws.
 */
export function readVersioned<T>({
  raw,
  version,
  current,
  upcasters,
  schema,
}: {
  raw: unknown;
  version: number | undefined;
  current: number;
  upcasters: Upcasters;
  schema: z.ZodType<T>;
}): ReadResult<T> {
  const from = version ?? 0;
  if (from > current) {
    return { type: "newer", version: from };
  }
  let body = raw;
  try {
    for (let at = from; at < current; at += 1) {
      const upcast = upcasters[at];
      if (upcast) {
        body = upcast(body);
      }
    }
  } catch (error) {
    return {
      type: "unreadable",
      version: from,
      reason: error instanceof Error ? error.message : String(error),
    };
  }
  const parsed = schema.safeParse(body);
  return parsed.success
    ? { type: "read", value: parsed.data }
    : { type: "unreadable", version: from, reason: parsed.error.message };
}
